/** 项目讨论纠错弹窗：撤回/删除先确认，原文仅在作者点重新编辑后读取，取消不覆盖现有草稿。 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "./ui/sonner";

/** target 指定主留言或回复及操作；onChanged 接收完整讨论快照，保留原 ID 与回复展开状态。 */
export function HubDiscussionMessageDialog({ target, projectId, api, zh, onClose, onChanged }) {
  const { discussion, reply, action } = target;
  const editing = action === "edit";
  const [draft, setDraft] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const path = `/projects/${projectId}/hub/discussions/${discussion.id}${reply ? `/replies/${reply.id}` : ""}`;

  useEffect(() => {
    if (!editing) return undefined;
    let active = true;
    api.get(`${path}/draft`).then(({ data }) => {
      if (active) { setDraft(data); setText(data.text); setFiles(data.files || []); }
    }).catch((e) => {
      if (active) setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : (zh ? "无法读取原文，请关闭后重试" : "Unable to load. Close and retry."));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, path, editing, zh]);

  const submit = async (event) => {
    event.preventDefault();
    if (pending.current || loading || (editing && !draft)) return;
    if (editing && !text.trim() && !files.length) { setError(zh ? "请填写内容或保留附件" : "Enter a message or keep an attachment"); return; }
    pending.current = true; setBusy(true); setError("");
    try {
      const { data } = action === "delete" ? await api.delete(path) : await api.post(`${path}/${editing ? "resend" : "recall"}`, editing ? {
        text, fileIds: files.map((f) => f.fileId), recalledAt: draft.recalledAt,
      } : {});
      onChanged(data);
      toast.success(editing ? (zh ? "已重新发送" : "Message resent") : action === "delete" ? (zh ? "已删除" : "Deleted") : (zh ? "已撤回，可重新编辑" : "Recalled. You can edit it again."));
      onClose();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : (zh ? "操作失败，请重试" : "Unable to save. Please retry."));
    } finally { pending.current = false; setBusy(false); }
  };
  const title = editing ? (zh ? "重新编辑" : "Edit recalled message") : action === "delete" ? (zh ? "删除这条消息？" : "Delete this message?") : (zh ? "撤回这条消息？" : "Recall this message?");
  return <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
    <DialogContent className="z-[71] max-w-lg" overlayClassName="z-[70]" closeDisabled={busy} closeLabel={zh ? "关闭消息操作" : "Close message action"} data-testid="hub-disc-message-dialog">
      <DialogHeader><DialogTitle>{title}</DialogTitle>
        <DialogDescription>{editing ? (zh ? "修改后重新发送。原有回复和待办保持关联。" : "Resend your correction. Replies and existing to-dos stay linked.") : (zh ? "项目群中的对应消息也会同步处理。其他人的回复和已创建的待办会保留。" : "The project chat will update too. Other replies and existing to-dos are kept.")}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        {editing && (loading ? <p role="status" className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "读取原文…" : "Loading original…"}</p> : draft && <>
          <div className="space-y-1.5"><label htmlFor="hub-disc-reedit-text" className="text-sm font-medium">{zh ? "消息内容" : "Message"}</label>
            <Textarea id="hub-disc-reedit-text" data-testid="hub-disc-reedit-text" value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={reply ? 500 : 1000} disabled={busy} />
          </div>
          {!!files.length && <div className="space-y-2" data-testid="hub-disc-reedit-files">{files.map((f) => <div key={f.fileId} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1 text-xs"><Paperclip className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 break-all">{f.filename}</span>
            <button type="button" disabled={busy} onClick={() => setFiles((prev) => prev.filter((x) => x.fileId !== f.fileId))} aria-label={`${zh ? "移除附件" : "Remove attachment"} ${f.filename}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:text-destructive focus-visible:ring-2 focus-visible:ring-brand-400"><X className="h-4 w-4" /></button>
          </div>)}</div>}
        </>)}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2"><Button type="button" variant="outline" disabled={busy} onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button type="submit" variant={editing ? "default" : "destructive"} disabled={busy || loading || (editing && !draft)} data-testid="hub-disc-message-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? (zh ? "重新发送" : "Resend") : action === "delete" ? (zh ? "删除" : "Delete") : (zh ? "撤回" : "Recall")}</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}
